import React from 'react';
import { Dialog, Tooltip, Flex, IconButton } from '@radix-ui/themes';
import { toast } from 'sonner';
import { observer } from 'mobx-react-lite';
import { CheckIcon, DeleteIcon, EditIcon, NotInterestedIcon, RemoveRedEyeIcon } from 'shared/icons';
import { ManagePlanCard } from '../manage-plan-card';
import { ManagePlan } from '../types';
import { PlanActionConfirmDialog } from '../plan-action-confirm-dialog';
import { usePlansManager } from '../plans-manager.provider';
import './plans-actions-menu.scss';
import { useNavigate } from 'react-router-dom';

type MangerPlanMenuAction = 'VIEW' | 'DEACTIVATE' | 'ACTIVATE' | 'EMPTY' | 'DELETE';

const PlansActionsMenuView = ({ plan }: { plan: ManagePlan }) => {
   const plansManager = usePlansManager();
   const navigate = useNavigate();
   const [open, setOpen] = React.useState<boolean>(false);
   const [action, setAction] = React.useState<MangerPlanMenuAction>('EMPTY');
   return (
      <Flex className="plans-actions-menu" gap="1" align="center">
         {
            {
               0: (
                  <Tooltip content="Activate" side="left">
                     <IconButton
                        color="red"
                        className="action-trigger"
                        onClick={() => {
                           setOpen(true);
                           setAction('ACTIVATE');
                        }}
                     >
                        <NotInterestedIcon width={20} height={20} />
                     </IconButton>
                  </Tooltip>
               ),

               1: (
                  <Tooltip content="Deactivate" side="left">
                     <IconButton
                        color="green"
                        className="action-trigger"
                        onClick={() => {
                           setOpen(true);
                           setAction('DEACTIVATE');
                        }}
                     >
                        <CheckIcon width={20} height={20} />
                     </IconButton>
                  </Tooltip>
               ),
            }[plan.status]
         }

         <Tooltip content="Edit order" side="top">
            <IconButton
               color="blue"
               className="action-trigger"
               highContrast
               onClick={() => {
                  navigate(`/admin/plans/editPlan/${plan.slug}`);
               }}
            >
               <EditIcon width={20} height={20} />
            </IconButton>
         </Tooltip>

         <Tooltip content="Delete" side="top">
            <IconButton
               color="red"
               className="action-trigger"
               onClick={() => {
                  setOpen(true);
                  setAction('DELETE');
               }}
            >
               <DeleteIcon width={20} height={20} />
            </IconButton>
         </Tooltip>

         <Tooltip content="View" side="top">
            <IconButton
               color="blue"
               className="action-trigger"
               highContrast
               onClick={() => {
                  setOpen(true);
                  setAction('VIEW');
               }}
            >
               <RemoveRedEyeIcon width={20} height={20} />
            </IconButton>
         </Tooltip>

         <Dialog.Root
            open={open}
            onOpenChange={(o) => {
               if (!o) {
                  setAction('EMPTY');
               }
               setOpen(o);
            }}
         >
            <Dialog.Content
               aria-describedby={undefined}
               onOpenAutoFocus={(e) => e.preventDefault()}
            >
               {
                  {
                     VIEW: <ManagePlanCard plan={plan} />,
                     DEACTIVATE: (
                        <PlanActionConfirmDialog
                           title="Deactivate Plan"
                           message="Do you want to Deactivate this Plan?"
                           loading={plansManager.deactivatePlanState.status === 'pending'}
                           onConfirm={() => {
                              plansManager.deactivatePlan({
                                 dto: { slug: plan.slug },
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success(resp['mesasge' as 'message']);
                                 },
                                 onError: (err) => {
                                    toast.error(err);
                                 },
                              });
                           }}
                        />
                     ),
                     ACTIVATE: (
                        <PlanActionConfirmDialog
                           title="Activate Plan"
                           message="Do you want to Activate this Plan?"
                           loading={plansManager.activatePlanState.status === 'pending'}
                           onConfirm={() => {
                              plansManager.activatePlan({
                                 dto: { slug: plan.slug },
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success(resp['mesasge' as 'message']);
                                 },
                                 onError: (err) => {
                                    toast.error(err);
                                 },
                              });
                           }}
                        />
                     ),
                     DELETE: (
                        <PlanActionConfirmDialog
                           title="Delete Plan"
                           message="Do you want to Delete this Plan?"
                           loading={plansManager.deletePlanState.status === 'pending'}
                           onConfirm={() => {
                              plansManager.deletePlan({
                                 dto: { slug: plan.slug },
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success(resp['message']);
                                 },
                                 onError: (err) => {
                                    toast.error(err);
                                 },
                              });
                           }}
                        />
                     ),
                     EMPTY: <></>,
                  }[action]
               }
            </Dialog.Content>
         </Dialog.Root>
      </Flex>
   );
};

export const PlansActionsMenu = observer(PlansActionsMenuView);
